import Icon from "@components/UI/Icon";
import Tooltip from "@components/UI/Tooltip";
import React from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { PaymentPlan } from "../paymentPlansData";
import { PRICE_PLANS, type Plan } from "../plansData";
import { StartButton } from "../StartButton";
import { FEATURES } from "./featuresData";

const Check = () => {
  return (
    <Icon
      name="present"
      color={["#6A6FD7", "#FFFFFF"]}
      size={16}
    />
  )
}

const NotPresent = () => {
  return (
    <Icon
      name="not-present"
      color={["#FF7E6D", "#FFFFFF"]}
      size={16}
    />
  )
}

const FeatureValue = ({ value }) => {
  if (value.type === "present") {
    return <Check />
  }

  if (value.type === "notPresent") {
    return <NotPresent />
  }

  return value.content
}

const scopeTitleClassName = "h-[52px] flex-0 px-[16px] pt-[17px] pb-[13px] flex items-center text-left text-neutral2-900 text-base font-semibold"

const featureCellClassName = "h-[60px] px-[24px] py-[19px] mb-[1px] flex flex-col justify-center"

const planFeatureCellClassName = twJoin(
  featureCellClassName,
  "bg-white",
  "items-center text-center text-neutral2-900 text-sm"
)

const stickyHeaderClassName = "sticky top-[55px] z-30"

export type FeaturesTableDesktopProps = {
  className?: string
  paymentPlan: PaymentPlan
}

type PlanHeaderProps = {
  paymentPlan: PaymentPlan
  plan: Plan
  planIndex: number
}

const PlanHeader = ({ paymentPlan, plan, planIndex }: PlanHeaderProps) => {
  return (
    <div className="w-[185px] shrink-0">
      <div className={stickyHeaderClassName}>
        {plan.recommended ? (
          <div className="bg-slate-blue-400 rounded-t-xl text-[12px] leading-[12px] py-[5px] h-[22px] font-semibold text-white text-center">
            BEST VALUE
          </div>
        ) : (
          <div className="h-[22px]" />
        )}

        <div
          className={twJoin(
            "h-[128px] pointer-events-auto",
            plan.recommended && "border-x-[2px] border-x-slate-blue-400"
          )}
        >
          <div
            className={twMerge(
              "h-[126px]",
              "bg-white flex flex-col items-center gap-[20px]",
              "px-[23px] pt-[20px] pb-[18px] mb-[1px]",
              "z-5",
              planIndex === 0 && "rounded-tl-xl",
              planIndex === PRICE_PLANS.length - 1 && "rounded-tr-xl",
              plan.recommended && "z-10"
            )}
          >
            <div className="text-neutral2-900 text-base font-semibold uppercase ">
              {plan.label}
            </div>
            <StartButton
              paymentPlan={paymentPlan}
              plan={plan}
              trialButtonChildren="Start free trial"
              salesButtonChildren="Contact us"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export function FeaturesTableDesktop({ className, paymentPlan }: FeaturesTableDesktopProps) {
  return (
    <div className={twMerge("relative flex items-stretch", className)}>
      <div className="w-[290px] shrink-0 pt-[149px]">
        {FEATURES.map((scope, scopeIndex) => (
          <React.Fragment key={scopeIndex}>
            {scope.scopeTitle && (
              <div className={scopeTitleClassName}>
                {scope.scopeTitle}
              </div>
            )}

            {scope.features.map((feature, featureIndex) => (
              <div
                key={featureIndex}
                className={twJoin(
                  featureCellClassName,
                  "bg-white mr-[1px]",
                  featureIndex === 0 && "rounded-tl-xl",
                  featureIndex === scope.features.length - 1 && "rounded-bl-xl"
                )}
              >
                <div className="flex items-center justify-between gap-[10px] text-neutral2-900 text-[15px] leading-[22px]">
                  <span>{feature.title}</span>
                  {feature.description && (
                    <Tooltip
                      title={feature.description}
                      triggerLabel={`Show ${feature.title} details`}
                    >
                      <Icon
                        name="info-circle-outlined"
                        color="neutral2-400"
                        size={16}
                      />
                    </Tooltip>
                  )}
                </div>
                {feature.description && (
                  <div className="sr-only">{feature.description}</div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>

      <div className="absolute bottom-[238px] left-[290px] right-0 top-0 z-30 flex pointer-events-none">
        {PRICE_PLANS.map((plan, planIndex) => (
          <PlanHeader
            key={plan.key}
            paymentPlan={paymentPlan}
            plan={plan}
            planIndex={planIndex}
          />
        ))}
      </div>

      {PRICE_PLANS.map((plan, planIndex) => (
        <div key={plan.key} className="w-[185px] shrink-0 flex flex-col">
          <div className="h-[149px] shrink-0" />

          <div
            className={twJoin(
              plan.recommended && "border-x-[2px] border-b-[2px] border-x-slate-blue-400 border-b-slate-blue-400 rounded-b-xl"
            )}
          >
            {FEATURES.map((scope, scopeIndex) => (
              <React.Fragment key={scopeIndex}>
                {scope.scopeTitle && (
                  <div className={scopeTitleClassName} />
                )}

                {scope.features.map((feature, featureIndex) => (
                  <div
                    key={featureIndex}
                    className={twJoin(
                      planFeatureCellClassName,
                      featureIndex === 0 && planIndex === PRICE_PLANS.length - 1 && scope.scopeTitle !== undefined && "rounded-tr-xl",
                      featureIndex === scope.features.length - 1 && planIndex == PRICE_PLANS.length - 1 && "rounded-br-xl",
                      plan.recommended && scopeIndex === FEATURES.length - 1 && featureIndex === scope.features.length - 1 && "rounded-b-xl"
                    )}
                  >
                    <FeatureValue value={feature.byPlan[plan.key]} />
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
